<template>
  <div class="task-form">
    <!-- Loading indicator while options are being fetched -->
    <div v-if="loadingOptions" class="task-form__loading">
      <theme-icon icon="loading" type="icons" classes="w-6 h-6 animate-spin" />
      <span>Loading form...</span>
    </div>

    <template v-else>
      <!-- Single grid form: title (col:4) → assignee (col:1) | priority | status | start_date → remaining -->
      <themed-form
        :fields="allFields"
        :model-value="formData"
        :errors="errors"
        :loading="loading"
        layout="grid"
        :columns="4"
        :validate-on-change="true"
        @input="handleInput"
      >
        <template #field-assignee_field>
          <div class="themed-form__field--col-3 themed-form-field themed-form-field--vertical">
            <label class="themed-form-field__label">
              <span class="themed-form-field__label-content">
                <span class="themed-form-field__label-text">Assigned To</span>
              </span>
            </label>
            <VueMultiselect
              v-model="selectedAssignee"
              :options="assigneeGroups"
              group-values="items"
              group-label="group"
              :searchable="true"
              :close-on-select="true"
              :allow-empty="true"
              :show-labels="false"
              track-by="key"
              label="label"
              placeholder="Select assignee..."
              class="themed-multiselect"
              @input="handleAssigneeChange"
            >
              <template #noResult>
                <span>No matches found</span>
              </template>
              <template #noOptions>
                <span>No assignees available</span>
              </template>
            </VueMultiselect>
            <!-- Company auto-fill: shown read-only when a Contact is selected -->
            <div
              v-if="formData.assignee_type === 'App\\Contact' && formData.company_name"
              class="themed-form-field themed-form-field--vertical mt-2"
            >
              <label class="themed-form-field__label">
                <span class="themed-form-field__label-text">Company</span>
              </label>
              <input
                type="text"
                :value="formData.company_name"
                readonly
                disabled
                class="w-full rounded-md border px-3 py-2 text-sm bg-muted text-muted-foreground cursor-not-allowed"
              />
            </div>
            <!-- Add new contact shortcut -->
            <div class="mt-1">
              <button
                type="button"
                class="text-xs text-primary hover:underline"
                @click="openNewContactModal"
              >+ Add new contact</button>
            </div>
          </div>
        </template>

        <template #field-project_id_display>
          <div class="themed-form__field--col-2 themed-form-field themed-form-field--vertical">
            <label class="themed-form-field__label">
              <span class="themed-form-field__label-text">Project</span>
            </label>
            <input
              type="text"
              :value="projectDisplayName"
              readonly
              disabled
              class="w-full rounded-md border px-3 py-2 text-sm bg-muted text-muted-foreground cursor-not-allowed"
            />
          </div>
        </template>
      </themed-form>

      <!-- Document Attachments Section -->
      <div class="mt-4 border-t pt-4">
        <div class="flex items-center justify-between mb-2">
          <h3 class="text-sm font-medium text-foreground">Attachments</h3>
          <div class="flex gap-2">
            <themed-button size="sm" variant="outline" type="button" @click="openDocumentPicker">
              Search Reopt Cloud
            </themed-button>
            <themed-button size="sm" variant="outline" type="button" :disabled="uploadingFile" @click="$refs.fileInput.click()">
              {{ uploadingFile ? 'Uploading...' : 'Upload File' }}
            </themed-button>
            <input type="file" class="hidden" ref="fileInput" @change="handleFileUpload" />
          </div>
        </div>

        <div v-if="attachedDocuments.length === 0 && pendingFiles.length === 0" class="text-sm text-muted-foreground">
          No attachments yet.
        </div>

        <ul v-else class="space-y-1">
          <!-- Saved attachments -->
          <li
            v-for="doc in attachedDocuments"
            :key="doc.id"
            class="flex items-center justify-between text-sm p-2 rounded-md bg-muted"
          >
            <a
              v-if="doc.view_url"
              :href="doc.view_url"
              target="_blank"
              class="text-primary hover:underline truncate"
            >{{ doc.name }}</a>
            <span v-else class="truncate">{{ doc.name }}</span>
            <button
              type="button"
              :disabled="doc._deleting"
              class="ml-4 flex-shrink-0 text-destructive hover:text-destructive/80 disabled:opacity-40"
              :aria-label="'Remove ' + doc.name"
              @click="detachDocument(doc)"
            >
              <theme-icon icon="trash" type="icons" classes="w-4 h-4" />
            </button>
          </li>

          <!-- Pending files (queued, not yet uploaded) -->
          <li
            v-for="(pf, idx) in pendingFiles"
            :key="'pending-' + idx"
            class="flex items-center justify-between text-sm p-2 rounded-md bg-muted opacity-70"
          >
            <span class="truncate">{{ pf.file.name }}</span>
            <div class="flex items-center gap-2 ml-4 flex-shrink-0">
              <span class="text-xs text-muted-foreground italic">Pending save</span>
              <button
                type="button"
                class="text-destructive hover:text-destructive/80"
                :aria-label="'Remove ' + pf.file.name"
                @click="removePendingFile(idx)"
              >
                <theme-icon icon="trash" type="icons" classes="w-4 h-4" />
              </button>
            </div>
          </li>
        </ul>
      </div>
    </template>

    <!-- Document picker modal -->
    <task-document-picker
      :task-id="task ? task.id : null"
      @document-attached="onDocumentAttached"
    />

    <!-- New Contact modal — reuses existing component, linked to task's project/site if available -->
    <new-contact-modal
      v-show="newContactAttachId"
      :attach-to="newContactAttachTo || 'project'"
      :attach-to-id="newContactAttachId || 0"
      @contact-created="handleNewContactCreated"
    />
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import VueMultiselect from 'vue-multiselect';
import ThemedForm from '../form/ThemedForm.vue';
import ThemedButton from '../ThemedButton.vue';
import ThemeIcon from '../ThemeIcon.vue';
import NewContactModal from '../contacts/NewContactModal.vue';
import TaskDocumentPicker from './TaskDocumentPicker.vue';

export default {
  name: 'TaskForm',

  components: {
    VueMultiselect,
    ThemedForm,
    ThemedButton,
    ThemeIcon,
    NewContactModal,
    TaskDocumentPicker,
  },

  props: {
    task: {
      type: Object,
      default: null,
    },
    projectId: {
      type: [Number, String],
      default: null,
    },
    siteId: {
      type: [Number, String],
      default: null,
    },
    statusId: {
      type: [Number, String],
      default: null,
    },
    parentId: {
      type: [Number, String],
      default: null,
    },
  },

  data() {
    return {
      formData: {
        title: '',
        description: '',
        assignee_type: null,
        assignee_id: null,
        company_name: null,
        priority_id: null,
        status_id: null,
        start_date: '',
        due_date: '',
        project_id: null,
        site_id: null,
        notes: '',
        parent_id: null,
      },
      // VueMultiselect selection object: { label, assignee_type, assignee_id, key }
      selectedAssignee: null,
      // Grouped options from /api/tasks/assignees
      assigneeGroups: [],
      projectOptions: [],
      siteOptions: [],
      loading: false,
      loadingOptions: true,
      errors: {},
      attachedDocuments: [],
      pendingFiles: [],       // Files queued for upload on new tasks (not yet saved)
      uploadingFile: false,
    };
  },

  computed: {
    ...mapGetters('tasks', ['activeStatuses', 'activePriorities']),

    isEditMode() {
      return this.task && this.task.id;
    },

    statusOptions() {
      return this.activeStatuses.map(s => ({ label: s.name, value: s.id }));
    },

    priorityOptions() {
      return this.activePriorities.map(p => ({ label: p.name, value: p.id }));
    },

    todayDate() {
      const today = new Date();
      const year  = today.getFullYear();
      const month = String(today.getMonth() + 1).padStart(2, '0');
      const day   = String(today.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    },

    minDueDateValue() {
      if (this.formData.start_date) {
        return this.formData.start_date >= this.todayDate
          ? this.formData.start_date
          : this.todayDate;
      }
      return this.todayDate;
    },

    projectDisplayName() {
      const pid = this.projectId || this.formData.project_id;
      if (!pid) return '';
      const found = this.projectOptions.find(p => p.value == pid);
      return found ? found.label : 'Project #' + pid;
    },

    // Determines what to attach a newly created contact to.
    // Prefer the task's project; fall back to site; default to project id 0 (no attach).
    newContactAttachTo() {
      const pid = this.projectId || this.formData.project_id;
      if (pid) return 'project';
      const sid = this.siteId || this.formData.site_id;
      if (sid) return 'site';
      return 'project';
    },

    newContactAttachId() {
      const pid = this.projectId || this.formData.project_id;
      if (pid) return Number(pid);
      const sid = this.siteId || this.formData.site_id;
      if (sid) return Number(sid);
      return null;
    },
    // either the form was opened from within a project (projectId prop set),
    // or we are editing an existing task that already has a project assigned.
    isProjectLocked() {
      return !!(this.projectId || (this.isEditMode && this.formData.project_id));
    },

    allFields() {
      const fields = [
        {
          name: 'title',
          type: 'input',
          inputType: 'text',
          label: 'Task',
          placeholder: 'Enter task name',
          required: true,
          col: 4,
          validation: { required: true, maxLength: 255 },
        },
        // Placeholder replaced by named slot #field-assignee_field
        { name: 'assignee_field', type: 'input', label: '', col: 4 },
        {
          name: 'status_id',
          type: 'select',
          label: 'Status',
          placeholder: 'Select status',
          options: this.statusOptions,
          col: 1,
        },
        {
          name: 'start_date',
          type: 'input',
          inputType: 'date',
          label: 'Start Date',
          col: 1,
          componentProps: {
            min: this.isEditMode ? undefined : this.todayDate,
          },
          validation: {
            customFn: (value) => {
              if (!this.isEditMode && value && value < this.todayDate) {
                return 'Start date cannot be in the past';
              }
              return true;
            },
          },
        },
        {
          name: 'due_date',
          type: 'input',
          inputType: 'date',
          label: 'Due Date',
          col: 1,
          componentProps: {
            min: this.minDueDateValue,
          },
          validation: {
            customFn: (value, formData) => {
              if (value && formData.start_date && value < formData.start_date) {
                return 'Due date must be after or equal to start date';
              }
              if (!this.isEditMode && value && value < this.todayDate) {
                return 'Due date cannot be in the past';
              }
              return true;
            },
          },
        },
        {
          name: 'priority_id',
          type: 'select',
          label: 'Priority',
          placeholder: 'Select priority',
          options: this.priorityOptions,
          col: 1,
        },
      ];

      if (!this.isProjectLocked) {
        fields.push({
          name: 'project_id',
          type: 'select',
          label: 'Project',
          placeholder: 'Select project (optional)',
          options: this.projectOptions,
          col: 2,
          componentProps: { clearable: true },
        });
      } else {
        fields.push({
          name: 'project_id_display',
          type: 'input',
          inputType: 'text',
          label: 'Project',
          col: 2,
        });
      }

      if (!this.siteId) {
        fields.push({
          name: 'site_id',
          type: 'select',
          label: 'Site',
          placeholder: 'Select site (optional)',
          options: this.siteOptions,
          col: 2,
          componentProps: { clearable: true },
        });
      }

      fields.push(
        {
          name: 'description',
          type: 'textarea',
          label: 'Description',
          placeholder: 'Enter task description',
          rows: 3,
          col: 4,
        },
        {
          name: 'notes',
          type: 'textarea',
          label: 'Notes',
          placeholder: 'Enter additional notes',
          rows: 3,
          col: 4,
        }
      );

      return fields;
    },
  },

  watch: {
    task: {
      immediate: true,
      deep: true,
      handler(newTask, oldTask) {
        if (oldTask && newTask && oldTask.id !== newTask.id) {
          this.loadingOptions = true;
          this.resetForm();
          this.$nextTick(() => {
            if (newTask && newTask.id) {
              this.populateForm(newTask);
            }
            this.loadingOptions = false;
          });
        } else if (newTask && newTask.id) {
          this.$nextTick(() => {
            this.populateForm(newTask);
          });
        }
      },
    },

    loading(newVal) {
      this.$emit('loading-change', newVal);
    },

    loadingOptions(newVal) {
      this.$emit('loading-options-change', newVal);
    },
  },

  async mounted() {
    this.loadingOptions = true;
    await this.loadFormOptions();
    this.loadingOptions = false;

    if (this.task && this.task.id) {
      this.populateForm(this.task);
      this.loadTaskDocuments();
    } else {
      this.initializeForm();
    }
  },

  methods: {
    async loadFormOptions() {
      try {
        await Promise.all([
          this.$store.dispatch('tasks/fetchStatuses'),
          this.$store.dispatch('tasks/fetchPriorities'),
        ]);

        const [assigneesRes, projectsRes, sitesRes] = await Promise.all([
          axios.get('/api/tasks/assignees'),
          axios.get('/api/projects/dropdown'),
          axios.get('/api/sites/dropdown'),
        ]);

        // Add composite key for VueMultiselect track-by
        // Append email in brackets when available
        this.assigneeGroups = (assigneesRes.data || []).map(group => ({
          ...group,
          items: (group.items || []).map(item => ({
            ...item,
            label: item.email ? `${item.label} (${item.email})` : item.label,
            key: `${item.assignee_type}:${item.assignee_id}`,
          })),
        }));

        this.projectOptions = projectsRes.data.map(p => ({ label: p.name, value: p.id }));
        this.siteOptions    = sitesRes.data.map(s => ({ label: s.name, value: s.id }));
      } catch (error) {
        console.error('Error loading form options:', error);
        this.$notify({
          type: 'error',
          title: 'Error',
          text: 'Failed to load form options. Please try again.',
        });
      }
    },

    resetForm() {
      this.selectedAssignee = null;
      this.formData = {
        title: '',
        description: '',
        assignee_type: null,
        assignee_id: null,
        company_name: null,
        priority_id: null,
        status_id: null,
        start_date: '',
        due_date: '',
        project_id: null,
        site_id: null,
        notes: '',
        parent_id: null,
      };
    },

    initializeForm() {
      if (this.projectId) this.formData.project_id = this.projectId;
      if (this.siteId)    this.formData.site_id    = this.siteId;
      if (this.statusId)  this.formData.status_id  = this.statusId;
      if (this.parentId)  this.formData.parent_id  = this.parentId;

      if (this.task) {
        if (this.task.due_date)   this.formData.due_date   = this.formatDateForInput(this.task.due_date);
        if (this.task.start_date) this.formData.start_date = this.formatDateForInput(this.task.start_date);
        if (this.task.status_id)  this.formData.status_id  = this.task.status_id;
      }
    },

    populateForm(task) {
      // Restore assignee selection from polymorphic fields
      if (task.assignee_type && task.assignee_id && task.assignee) {
        this.selectedAssignee = {
          label:         task.assignee.name,
          assignee_type: task.assignee_type,
          assignee_id:   task.assignee_id,
          key:           `${task.assignee_type}:${task.assignee_id}`,
        };
      } else {
        this.selectedAssignee = null;
      }

      this.formData = {
        title:         task.title || '',
        description:   task.description || '',
        assignee_type: task.assignee_type || null,
        assignee_id:   task.assignee_id || null,
        priority_id:   task.priority_id || (task.priority ? task.priority.id : null),
        status_id:     task.status_id || (task.status ? task.status.id : null),
        start_date:    this.formatDateForInput(task.start_date),
        due_date:      this.formatDateForInput(task.due_date),
        project_id:    task.project_id || (task.project ? task.project.id : null),
        site_id:       task.site_id || (task.site ? task.site.id : null),
        notes:         task.notes || '',
        parent_id:     task.parent_id || null,
      };
    },

    handleAssigneeChange(selected) {
      if (selected) {
        this.formData.assignee_type = selected.assignee_type;
        this.formData.assignee_id   = selected.assignee_id;
        this.formData.company_name  = selected.assignee_type === 'App\\Contact'
          ? (selected.company_name || null)
          : null;
      } else {
        this.formData.assignee_type = null;
        this.formData.assignee_id   = null;
        this.formData.company_name  = null;
      }
    },

    formatDateForInput(dateString) {
      if (!dateString) return '';
      if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) return dateString;
      try {
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return '';
        const year  = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day   = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      } catch {
        return '';
      }
    },

    handleInput(updatedData) {
      // Preserve assignee fields (managed separately by VueMultiselect)
      this.formData = {
        ...updatedData,
        assignee_type: this.formData.assignee_type,
        assignee_id:   this.formData.assignee_id,
        company_name:  this.formData.company_name,
      };
    },

    async handleSubmit() {
      if (this.loading) return;

      this.loading = true;
      this.errors  = {};

      try {
        let response;
        // Strip display-only fields before submitting
        const { company_name, project_id_display, ...payload } = this.formData;
        if (this.isEditMode) {
          response = await this.$store.dispatch('tasks/updateTask', {
            id:   this.task.id,
            data: payload,
          });
        } else {
          response = await this.$store.dispatch('tasks/createTask', payload);
          // Upload any files that were queued before the task existed
          if (response && response.id) {
            await this.uploadPendingFiles(response.id);
          }
        }

        this.$notify({
          type:  'success',
          title: 'Success',
          text:  this.isEditMode ? 'Task updated successfully' : 'Task created successfully',
        });

        this.$emit('save', response);
      } catch (error) {
        if (error.response?.data?.errors) {
          this.errors = error.response.data.errors;
        }
        this.$notify({
          type:  'error',
          title: 'Error',
          text:  error.response?.data?.message || 'An error occurred while saving the task.',
        });
      } finally {
        this.loading = false;
      }
    },

    handleCancel() {
      this.$emit('cancel');
    },

    // ─── Document Attachment Methods ────────────────────────────────

    async loadTaskDocuments() {
      if (!this.task || !this.task.id) return;
      try {
        const res = await axios.get(`/api/tasks/${this.task.id}/documents`);
        this.attachedDocuments = res.data;
      } catch (e) {
        console.error('Failed to load task documents:', e);
      }
    },

    openDocumentPicker() {
      if (!this.task || !this.task.id) {
        this.$toast.warning('Please save the task before attaching documents.');
        return;
      }
      this.$modal.show('task-document-picker');
    },

    onDocumentAttached(doc) {
      this.attachedDocuments.push(doc);
    },

    async handleFileUpload(event) {
      const file = event.target.files[0];
      if (!file) return;

      // New unsaved task — queue the file, upload after save
      if (!this.task || !this.task.id) {
        this.pendingFiles.push({ file });
        this.$refs.fileInput.value = '';
        return;
      }

      // Existing task — upload immediately
      this.uploadingFile = true;
      const formData = new FormData();
      formData.append('file', file);
      try {
        const res = await axios.post(`/api/tasks/${this.task.id}/documents/upload`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        this.attachedDocuments.push(res.data);
        this.$toast.success(`${file.name} uploaded successfully.`);
      } catch (e) {
        this.$toast.error('File upload failed.');
      } finally {
        this.uploadingFile = false;
        this.$refs.fileInput.value = '';
      }
    },

    removePendingFile(idx) {
      this.pendingFiles.splice(idx, 1);
    },

    async uploadPendingFiles(taskId) {
      if (!this.pendingFiles.length) return;
      for (const pf of this.pendingFiles) {
        const formData = new FormData();
        formData.append('file', pf.file);
        try {
          const res = await axios.post(`/api/tasks/${taskId}/documents/upload`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
          });
          this.attachedDocuments.push(res.data);
        } catch (e) {
          this.$toast.error(`Failed to upload ${pf.file.name}.`);
        }
      }
      this.pendingFiles = [];
    },

    async detachDocument(doc) {
      if (!this.task || !this.task.id || doc._deleting) return;
      this.$set(doc, '_deleting', true);
      try {
        await axios.delete(`/api/tasks/${this.task.id}/documents/${doc.id}`);
        this.attachedDocuments = this.attachedDocuments.filter(d => d.id !== doc.id);
        this.$toast.success(`${doc.name} removed.`);
      } catch (e) {
        this.$set(doc, '_deleting', false);
        this.$toast.error('Failed to remove attachment.');
      }
    },

    openNewContactModal() {
      const attachId = this.newContactAttachId;
      if (!attachId) {
        this.$toast.warning('Please select a project before adding a new contact.');
        return;
      }
      this.$modal.show('contact-new');
    },

    async handleNewContactCreated(newContact) {
      // Re-fetch assignees so the new contact appears in the dropdown
      try {
        const res = await axios.get('/api/tasks/assignees');
        this.assigneeGroups = (res.data || []).map(group => ({
          ...group,
          items: (group.items || []).map(item => ({
            ...item,
            label: item.email ? `${item.label} (${item.email})` : item.label,
            key: `${item.assignee_type}:${item.assignee_id}`,
          })),
        }));
      } catch (e) {
        console.error('Failed to refresh assignees after contact creation:', e);
      }

      // Auto-select the newly created contact as the assignee
      const contactLabel = newContact.email
        ? `${newContact.name} (${newContact.email})`
        : newContact.name;
      const newAssignee = {
        label:         contactLabel,
        assignee_type: 'App\\Contact',
        assignee_id:   newContact.id,
        key:           `App\\Contact:${newContact.id}`,
        email:         newContact.email || null,
      };
      this.selectedAssignee = newAssignee;
      this.handleAssigneeChange(newAssignee);
    },
  },
};
</script>
